import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ProfileMode = 'view' | 'edit-profile' | 'edit-password';

export type ProfileDraft = {
  displayName: string;
  timezone: string;
  preferredLocale: string | null;
};

export type PasswordDraft = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type ProfilePageState = {
  mode: ProfileMode;
  profileDraft: ProfileDraft;
  passwordDraft: PasswordDraft;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const emptyDraft: ProfileDraft = {
  displayName: '',
  timezone: '',
  preferredLocale: null,
};

const emptyPasswordDraft: PasswordDraft = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const initialState: ProfilePageState = {
  mode: 'view',
  profileDraft: emptyDraft,
  passwordDraft: emptyPasswordDraft,
  submitInProgress: false,
  lastErrorKey: null,
};

export const profilePageSlice = createSlice({
  name: 'profilePage',
  initialState,
  reducers: {
    setMode(state, action: PayloadAction<ProfileMode>) {
      state.mode = action.payload;
      if (action.payload === 'view') {
        state.profileDraft = emptyDraft;
        state.passwordDraft = emptyPasswordDraft;
        state.lastErrorKey = null;
      }
    },
    prefillProfileDraft(state, action: PayloadAction<ProfileDraft>) {
      state.profileDraft = action.payload;
      state.lastErrorKey = null;
    },
    setDraftDisplayName(state, action: PayloadAction<string>) {
      state.profileDraft.displayName = action.payload;
    },
    setDraftTimezone(state, action: PayloadAction<string>) {
      state.profileDraft.timezone = action.payload;
    },
    setDraftPreferredLocale(state, action: PayloadAction<string | null>) {
      state.profileDraft.preferredLocale = action.payload;
    },
    setDraftCurrentPassword(state, action: PayloadAction<string>) {
      state.passwordDraft.currentPassword = action.payload;
    },
    setDraftNewPassword(state, action: PayloadAction<string>) {
      state.passwordDraft.newPassword = action.payload;
    },
    setDraftConfirmPassword(state, action: PayloadAction<string>) {
      state.passwordDraft.confirmPassword = action.payload;
    },
    clearPasswordDraft(state) {
      state.passwordDraft = emptyPasswordDraft;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    resetPage() {
      return initialState;
    },
  },
});

export const {
  setMode,
  prefillProfileDraft,
  setDraftDisplayName,
  setDraftTimezone,
  setDraftPreferredLocale,
  setDraftCurrentPassword,
  setDraftNewPassword,
  setDraftConfirmPassword,
  clearPasswordDraft,
  setLastErrorKey,
  setSubmitInProgress,
  resetPage,
} = profilePageSlice.actions;

type AppRootState = { profilePage: ProfilePageState };

export const selectProfilePage = (state: AppRootState) => state.profilePage;
